import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useEffect, useRef, useState } from "react";
import type { HelpArticleVideo as HelpArticleVideoData } from "@/interfaces/help";
import { formatVideoDuration, toHelpVideoEmbedUrl } from "@/utils/help-video-helper";

interface HelpArticleVideoProps {
  slug: string;
  title: string;
  video: HelpArticleVideoData;
}

// Slug + url pairs already warned about: StrictMode's double effect run and reopening the article
// would otherwise repeat the warning for the same bad link.
const warnedUnrecognisedUrls = new Set<string>();

function withAutoplay(embedUrl: string): string {
  const url = new URL(embedUrl);
  url.searchParams.set("autoplay", "1");
  return url.toString();
}

/**
 * The article's walkthrough video, per `.hc-video` (content/design/client-experience-prototype.html):
 * a click-to-play tile showing the duration and optional poster. No third-party request is made
 * until the reader clicks; only then does the player iframe mount.
 */
export function HelpArticleVideo({ slug, title, video }: HelpArticleVideoProps) {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const embedUrl = toHelpVideoEmbedUrl(video.url);

  useEffect(() => {
    if (embedUrl !== null || !video.url) return;
    const key = `${slug}\n${video.url}`;
    if (warnedUnrecognisedUrls.has(key)) return;
    warnedUnrecognisedUrls.add(key);
    console.warn(
      `[help] article "${slug}": video url is not a YouTube/Vimeo link, not embedding: ${video.url}`,
    );
  }, [slug, video.url, embedUrl]);

  useEffect(() => {
    if (playing) iframeRef.current?.focus();
  }, [playing]);

  if (embedUrl === null) return null;

  const duration =
    typeof video.durationSeconds === "number" ? formatVideoDuration(video.durationSeconds) : null;

  if (playing) {
    return (
      <div className="relative mb-5 aspect-video w-full overflow-hidden rounded-[10px] border border-smoke bg-light-dust">
        <iframe
          ref={iframeRef}
          src={withAutoplay(embedUrl)}
          title={`${title} video`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={duration ? `Play video: ${title}, ${duration}` : `Play video: ${title}`}
      className="relative mb-5 grid aspect-video w-full cursor-pointer place-items-center overflow-hidden rounded-[10px] border border-smoke bg-light-dust p-0 transition-colors hover:border-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2"
    >
      {video.posterUrl && (
        <img
          src={`${video.posterUrl}?w=1280&fit=max&auto=format`}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      )}
      <span className="relative grid h-14 w-14 place-items-center rounded-full bg-rodeo-black text-white">
        <PlayArrowIcon sx={{ fontSize: 28, ml: "3px" }} aria-hidden="true" />
      </span>
      {duration && (
        <span className="absolute bottom-3 left-3 rounded bg-[rgba(51,51,51,.88)] px-2 py-[3px] font-bold text-[11px] text-white tracking-[.04em]">
          {duration}
        </span>
      )}
    </button>
  );
}

export default HelpArticleVideo;
